/**
 * Listings API endpoints
 */
import { apiClient } from '../api-client';
import {
  Listing,
  CreateListingRequest,
  ListingFilter,
  ListingResponse,
} from '@/types/listing';
import { PaginationParams } from '@/types/common';

const getFavoritesKey = () => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload?.sub) {
        return `memads_favorites_${payload.sub}`;
      }
    } catch (e) {
      console.error('Error decoding access token for favorites key:', e);
    }
  }
  return 'memads_favorites_guest';
};

export const listingsAPI = {
  /**
   * Get all listings with optional filters.
   * NOTE: The backend uses `q` as the search param (not `search`),
   * so we remap `search` → `q` here before sending to the API.
   */
  getAll: (filters?: Partial<ListingFilter & PaginationParams>) => {
    const { search, category, limit, ...rest } = filters ?? {};
    const params: any = { ...rest };
    if (search) params.q = search;
    if (category) params.category = category.toUpperCase();
    if (limit) params.size = limit;
    return apiClient.get<ListingResponse>('/api/listings', { params });
  },

  /**
   * Get a single listing by ID
   */
  getById: (id: string) =>
    apiClient.get<Listing>(`/api/listings/${id}`),

  /**
   * Get listings by user ID (seller's listings)
   */
  getByUserId: (userId: string, filters?: Partial<PaginationParams>) =>
    apiClient.get<ListingResponse>(`/api/listings/user/${userId}`, { params: filters }),

  /**
   * Get seller's own listings with status filters
   * Maps to GET /api/listings/seller/{sellerId}
   */
  getSellerListings: (sellerId: string, filters?: Partial<PaginationParams>) =>
    apiClient.get<ListingResponse>(`/api/listings/seller/${sellerId}`, { params: filters }),

  /**
   * Update listing status (e.g., mark as SOLD)
   */
  updateListingStatus: (id: string, _status: string) =>
    apiClient.put<Listing>(`/api/listings/${id}/mark-sold`, {}),

  /**
   * Create a new listing
   */
  create: (data: CreateListingRequest) =>
    apiClient.post<Listing>('/api/listings', data),

  /**
   * Update an existing listing
   */
  update: (id: string, data: Partial<CreateListingRequest>) =>
    apiClient.put<Listing>(`/api/listings/${id}`, data),

  /**
   * Delete a listing
   */
  delete: (id: string) =>
    apiClient.delete<{ success: boolean }>(`/api/listings/${id}`),

  /**
   * Search listings
   */
  search: (query: string, filters?: Partial<ListingFilter & PaginationParams>) => {
    const { category, ...rest } = filters ?? {};
    const params: any = { ...rest };
    if (category) params.category = category.toUpperCase();
    return apiClient.get<ListingResponse>('/api/listings/search', {
      params: { q: query, ...params },
    });
  },

  /**
   * Get featured listings
   */
  getFeatured: (limit: number = 10, category?: string, location?: string) =>
    apiClient.get<{ listings: Listing[] }>('/api/listings/featured', {
      params: { 
        limit, 
        category: category ? category.toUpperCase() : undefined, 
        location 
      },
    }),

  /**
   * Like/unlike a listing
   */
  toggleLike: async (id: string, listingData?: any) => {
    const key = getFavoritesKey();
    const favoritesJSON = localStorage.getItem(key);
    let favorites: any[] = [];
    if (favoritesJSON) {
      try {
        favorites = JSON.parse(favoritesJSON);
      } catch (e) {
        favorites = [];
      }
    }

    const index = favorites.findIndex((f) => f.id === id);
    if (index > -1) {
      favorites.splice(index, 1);
      localStorage.setItem(key, JSON.stringify(favorites));
      return { liked: false };
    } else {
      if (listingData) {
        favorites.push(listingData);
        localStorage.setItem(key, JSON.stringify(favorites));
        return { liked: true };
      } else {
        try {
          const listing = await listingsAPI.getById(id);
          favorites.push(listing);
          localStorage.setItem(key, JSON.stringify(favorites));
          return { liked: true };
        } catch (err) {
          favorites.push({ id });
          localStorage.setItem(key, JSON.stringify(favorites));
          return { liked: true };
        }
      }
    }
  },

  /**
   * Get user's liked listings
   */
  getLiked: async (filters?: Partial<PaginationParams>) => {
    const key = getFavoritesKey();
    const favoritesJSON = localStorage.getItem(key);
    let favorites: any[] = [];
    if (favoritesJSON) {
      try {
        favorites = JSON.parse(favoritesJSON);
      } catch (e) {
        favorites = [];
      }
    }
    return favorites as any;
  },

  /**
   * Check if a listing is liked
   */
  isLiked: (id: string): boolean => {
    const key = getFavoritesKey();
    const favoritesJSON = localStorage.getItem(key);
    if (!favoritesJSON) return false;
    try {
      const favorites: any[] = JSON.parse(favoritesJSON);
      return favorites.some((f) => f.id === id);
    } catch (e) {
      return false;
    }
  },

  /**
   * Filter listings by category
   */
  getByCategory: (category: string, filters?: Partial<PaginationParams>) =>
    apiClient.get<ListingResponse>(`/api/listings/category/${category}`, {
      params: filters,
    }),

  /**
   * Get active listing counts grouped by location (city)
   */
  getLocationCounts: (): Promise<Record<string, number>> =>
    apiClient.get<Record<string, number>>('/api/listings/location-counts'),

  /**
   * Get active listing counts grouped by category
   */
  getCategoryCounts: (): Promise<Record<string, number>> =>
    apiClient.get<Record<string, number>>('/api/listings/category-counts'),
};

